/**
 * "Cover and replace" text editing: covers an existing text run with a patch
 * and draws the replacement on top, rather than truly rewriting the PDF's
 * content-stream text objects. That's a deliberate choice, not a shortcut —
 * true in-place editing is unreliable across real-world PDFs: most PDFs
 * embed only the glyphs they actually use ("subsetted" fonts), so typing a
 * character the original font never needed has no glyph to render; and PDF
 * has no text reflow, so changing a run's length just overlaps or gaps
 * neighboring text either way. Cover-and-replace sidesteps both: pick any
 * font that has the glyphs you need, and only ever affects the exact box
 * you edited.
 *
 * Font matching itself (trying the page's own embedded fonts before falling
 * back to a standard font) lives in pageFonts.ts, shared with addText.ts.
 */
import { PDFDocument, PDFFont, rgb } from 'pdf-lib';
// @ts-ignore — no published types; the runtime default export is the fontkit instance pdf-lib expects.
import fontkit from '@pdf-lib/fontkit';
import { ProcessedPage, TextEdit } from './pdfProcessor';
import { PageFontCandidate, resolveFontForText, embedStandardFallback } from './pageFonts';

export interface BakeTextEditsResult {
  buffer: ArrayBuffer;
  /** True if every edit found a page font that truly covers its text — for
   * surfacing "used an approximate font" in the UI when false for any edit. */
  allExactMatches: boolean;
}

async function bakeOnce(arrayBuffer: ArrayBuffer, pages: ProcessedPage[], useExtractedFonts: boolean): Promise<BakeTextEditsResult> {
  const doc = await PDFDocument.load(arrayBuffer.slice(0), { ignoreEncryption: true });
  doc.registerFontkit(fontkit);
  const candidateCache = new Map<number, PageFontCandidate[]>();
  const fallbackCache = new Map<string, PDFFont>();
  let allExactMatches = true;

  for (const p of pages) {
    if (!p.textEdits?.length) continue;
    const page = doc.getPage(p.pageIndex);
    for (const edit of p.textEdits) {
      const { font, isExactMatch } = useExtractedFonts
        ? await resolveFontForText(doc, page, p.pageIndex, edit.newText, edit.fontFamilyHint, candidateCache, fallbackCache)
        : { font: await embedStandardFallback(doc, edit.fontFamilyHint, fallbackCache), isExactMatch: false };
      if (!isExactMatch) allExactMatches = false;
      // Cover with generous padding for descenders/ascenders (pdf.js's item
      // height approximates the font bbox, not exact per-glyph extents).
      const [bgR, bgG, bgB] = edit.backgroundColor ?? [1, 1, 1];
      const [fgR, fgG, fgB] = edit.textColor ?? [0, 0, 0];
      page.drawRectangle({
        x: edit.x - 1,
        y: edit.y - edit.height * 0.3,
        width: edit.width + 2,
        height: edit.height * 1.4,
        color: rgb(bgR, bgG, bgB),
      });
      page.drawText(edit.newText, {
        x: edit.x,
        y: edit.y,
        size: edit.fontSize,
        font,
        color: rgb(fgR, fgG, fgB),
      });
    }
  }

  const bytes = await doc.save();
  const buffer = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
  return { buffer, allExactMatches };
}

export async function bakeTextEdits(arrayBuffer: ArrayBuffer, pages: ProcessedPage[]): Promise<BakeTextEditsResult> {
  const hasEdits = pages.some(p => p.textEdits && p.textEdits.length > 0);
  if (!hasEdits) return { buffer: arrayBuffer, allExactMatches: true };

  try {
    return await bakeOnce(arrayBuffer, pages, true);
  } catch (err) {
    // Embedding/subsetting a real-world font can fail in ways specific to
    // that exact font file (seen: a legacy Mac TrueType `post` table variant
    // fontkit's subset builder chokes on even after sanitizing the header —
    // rare, but export must never just break because of it). Retry once with
    // only pdf-lib's built-in standard fonts — no custom embedding at all —
    // so the edit always makes it into the export, just with a less exact
    // font match, rather than the whole export failing.
    console.error('bakeTextEdits: custom font embedding failed, retrying with standard fonts only:', err);
    return bakeOnce(arrayBuffer, pages, false);
  }
}

/** Stable id for a newly-created edit (not the pdf.js item index, which
 * isn't stable across re-renders — just needs to be unique within a page). */
export function makeTextEditId(): string {
  return `te_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

export type { TextEdit };
